import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as styles from './CommonService.css';
import { ICommonServiceContainerProps } from './types';
import { EBetPositionStatus, EServiceId, selectPublishTermsService } from '../../../../redux/reducers';
import {
  selectActiveServiceId,
  selectDefaultServiceId,
  selectPromotionServiceId,
  selectActiveOrPromotionServiceId,
} from '../../../../selectors/publishTerms';
import { selectIsNewOffer } from '../../../../selectors/offer';
import { TDispatch } from '../../../../redux';
import {
  selectBaseAuctionBetByServiceId,
  selectCurrentBet,
  selectIsAuctionEnabled,
  selectIsBetChanged,
} from '../../../../selectors/auction';
import { formatMoney } from '../../../../utils/string';
import { ServicePrice } from './components/ServicePrice';
import {
  InitialBetService,
  PremiumBetService,
  PremiumService,
  StandardBetService,
  StandardService,
  TopBetService,
  TopService,
  WithoutBetService,
} from './components/services';
import { BetInputContainer } from '../BetInput';
import { BetPositionLayout } from './components/BetPositionLayout';
import { selectBetPositionStatus } from '../../../../selectors/betPosition';
import { useIsMobileDevice } from '../../../../hooks';
import { STANDARD_SERVICES } from '../../../../constants/services';
import { getBetPositionText } from '../../utils/getBetPositionText';

export const CommonServiceContainer = (props: ICommonServiceContainerProps) => {
  const { serviceId } = props;

  const isNewOffer = useSelector(selectIsNewOffer);
  const selectedServiceId = useSelector(selectActiveServiceId)!;
  const baseAuctionBet = useSelector(selectBaseAuctionBetByServiceId(serviceId));
  const isAuctionEnabled = useSelector(selectIsAuctionEnabled);
  const promotionServiceId = useSelector(selectPromotionServiceId);
  const activeOrPromotionServiceId = useSelector(selectActiveOrPromotionServiceId);
  const betPositionStatus = useSelector(selectBetPositionStatus);
  const currentBet = useSelector(selectCurrentBet);
  const isBetChanged = useSelector(selectIsBetChanged);
  const defaultServiceId = useSelector(selectDefaultServiceId);

  const dispatch = useDispatch<TDispatch>();

  const isMobile = useIsMobileDevice();
  const isActive = serviceId === selectedServiceId;

  const onCardClick = React.useCallback(() => {
    if (isActive) {
      return;
    }

    dispatch(selectPublishTermsService(serviceId));
  }, [isActive, dispatch]);

  const commonProps = {
    checked: isActive,
    onClick: onCardClick,
  };

  const formattedBet = formatMoney(baseAuctionBet);

  // На мобилках позиция отображается внутри PromotionSectionContainer
  const betPosition = !isMobile && isActive && (
    <BetPositionLayout
      loading={betPositionStatus === EBetPositionStatus.pending}
      betPosition={getBetPositionText({ serviceId: activeOrPromotionServiceId, currentBet })}
    />
  );

  const servicePrice = <ServicePrice price={formattedBet} />;

  const getRendererService = () => {
    switch (serviceId) {
      case EServiceId.top:
        return <TopService {...commonProps} footer={servicePrice} />;

      case EServiceId.premium:
        return <PremiumService {...commonProps} footer={servicePrice} />;
    }

    if (!isAuctionEnabled) {
      return <StandardService {...commonProps} footer={servicePrice} />;
    }

    const isStandardDefault = STANDARD_SERVICES.includes(defaultServiceId as EServiceId);

    /**
     * Отображаем "Выберите ставку", если ставка не была изменена после первого рендера и:
     * 1) Объявление создается
     * либо
     * 2) Объявление редактируется и не было размещено в стандартном
     */
    if (!isBetChanged && (isNewOffer || !isStandardDefault)) {
      return <InitialBetService {...commonProps} footer={<BetInputContainer />} />;
    }

    if (!currentBet) {
      return <WithoutBetService {...commonProps} footer={<BetInputContainer />} />;
    }

    switch (promotionServiceId) {
      case EServiceId.top:
        return <TopBetService {...commonProps} footer={<BetInputContainer />} />;

      case EServiceId.premium:
        return <PremiumBetService {...commonProps} footer={<BetInputContainer />} />;

      default:
        return <StandardBetService {...commonProps} footer={<BetInputContainer />} />;
    }
  };

  return (
    <div className={styles['common-service-container']}>
      {betPosition}
      {getRendererService()}
    </div>
  );
};
